import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Field, useField, useForm } from "react-final-form";
import { AdminSelectAdapter } from "components/select";
import type { SelectOption } from "./subscriptionFormUtils";

export interface DiscoverCountriesFieldHandle {
	hasPendingSelection: () => boolean;
}

interface DiscoverCountriesFieldProps {
	discoverCountryOptions: SelectOption[];
	subscriptionRowId?: number | null;
}

export const DiscoverCountriesField = forwardRef<DiscoverCountriesFieldHandle, DiscoverCountriesFieldProps>(
	function DiscoverCountriesField({ discoverCountryOptions, subscriptionRowId }, ref) {
		const form = useForm();
		const discoverCountriesField = useField<SelectOption[] | null>("discoverCountries");
		const [wantsSelected, setWantsSelected] = useState(false);
		const discoverCountriesRef = useRef<SelectOption[] | null>(null);

		useEffect(() => {
			const initialDiscoverCountries = form.getState().initialValues?.discoverCountries as
				| SelectOption[]
				| null
				| undefined;
			setWantsSelected((initialDiscoverCountries?.length ?? 0) > 0);
		}, [subscriptionRowId, form]);

		useImperativeHandle(
			ref,
			() => ({
				hasPendingSelection: () => {
					const hasSelection = (discoverCountriesRef.current?.length ?? 0) > 0;
					return wantsSelected && hasSelection === false;
				},
			}),
			[wantsSelected],
		);

		const selectedCountries = discoverCountriesField.input.value;
		discoverCountriesRef.current = selectedCountries ?? null;
		const hasSelection = (selectedCountries?.length ?? 0) > 0;
		const showCountryPicker = hasSelection || wantsSelected;

		const handleAllCountries = () => {
			form.change("discoverCountries", null);
			setWantsSelected(false);
		};

		const handleSelectedCountries = () => {
			setWantsSelected(true);
		};

		return (
			<div className="subscriptionEditForm-field subscriptionEditForm-field--full">
				<div className="subscriptionEditForm-discoverCountriesHeader">
					<div className="cellTitle">Discover countries</div>
					<div className="subscriptionEditForm-discoverCountriesRadios">
						<label className="subscriptionEditForm-discoverCountriesRadio">
							<input
								type="radio"
								name="discoverCountriesSelection"
								checked={showCountryPicker === false}
								onChange={handleAllCountries}
							/>
							<span>All countries</span>
						</label>
						<label className="subscriptionEditForm-discoverCountriesRadio">
							<input
								type="radio"
								name="discoverCountriesSelection"
								checked={showCountryPicker}
								onChange={handleSelectedCountries}
							/>
							<span>Selected countries</span>
						</label>
					</div>
				</div>
				{showCountryPicker ? (
					<div className="subscriptionEditForm-input">
						<Field
							name="discoverCountries"
							component={AdminSelectAdapter}
							isMulti
							options={discoverCountryOptions}
						/>
					</div>
				) : null}
			</div>
		);
	},
);
